import React from 'react';
import PropTypes from 'prop-types';

import MessageDetailsShape from 'app/shapes/MessageDetailsShape';

import {
  Box,
  Stack,
} from '@mui/material';
import { styled } from '@mui/material/styles';

import MessageDetailsControls from 'app/features/mail/components/message_details/MessageDetailsControls';
import MessageDetailsHeader from 'app/features/mail/components/message_details/MessageDetailsHeader';
import PreviewableAttachments from 'app/features/mail/components/PreviewableAttachments';
import FullPageLoadingSpinner from 'app/features/mail/components/FullPageLoadingSpinner';
import ListingButton from 'app/features/mail/components/message_details/ListingButton';
import SanitizedHTML from 'app/helpers/SanitizedHTML';

const MessageDetailRow = styled(Box)(() => ({
  '&.MuiBox-root': {
    borderBottom: '1px solid #ddd',
    padding: '8px',
  },
}));

const MessageDetails = ({
  messageDetails,
  isLoading,
  closeFunc,
  currentUser,
  selectedTab,
  maxHeight,
}) => {
  // NOTE: Once loading is finished, the messageDetails still must be set by a hook, so we may need one more round trip.
  if (isLoading || !messageDetails) {
    return (
      <FullPageLoadingSpinner />
    );
  }

  const messageTypeRequiresPaymentToView = messageDetails.messageType === 'Pay-to-View';
  const requiresPaymentToView = currentUser.id !== messageDetails.sender.id && !messageDetails.isPaid && messageTypeRequiresPaymentToView;

  return (
    <Box
      sx={{
        maxHeight,
        overflowY: 'auto',
      }}
    >
      <MessageDetailsControls
        closeFunc={closeFunc}
        selectedTab={selectedTab}
      />
      <Stack
        direction="column"
      >
        { requiresPaymentToView && (
          <MessageDetailRow>
            Send Payment
          </MessageDetailRow>
        )}
        <MessageDetailRow>
          <MessageDetailsHeader
            messageDetails={messageDetails}
            currentUser={currentUser}
            selectedTab={selectedTab}
          />
        </MessageDetailRow>
        {messageDetails.listing && (
          <MessageDetailRow>
            <ListingButton
              listing={messageDetails.listing}
            />
          </MessageDetailRow>
        )}
        {messageDetails.description && (
          <MessageDetailRow>
            {messageDetails.description}
          </MessageDetailRow>
        )}
        { !requiresPaymentToView && (
          <>
            <MessageDetailRow>
              <SanitizedHTML html={messageDetails.body} />
            </MessageDetailRow>
            {messageDetails.attachments.length > 0 && (
              <MessageDetailRow>
                <h3>
                  Attachments
                </h3>
                <PreviewableAttachments attachments={messageDetails.attachments} />
              </MessageDetailRow>
            )}
          </>
        )}
        { requiresPaymentToView && (
          <MessageDetailRow>
            <h3>
              Attachments
            </h3>
            <Box>
              This message is locked. Please pay to unlock.
              { messageDetails.attachments.map((attachment) => (
                <>
                  <Box key={attachment.id}>
                    {attachment.filename}
                  </Box>
                  <Box>
                    {attachment.sizeString}
                  </Box>
                </>
              ))}
            </Box>
          </MessageDetailRow>
        )}
      </Stack>
    </Box>
  );
};

MessageDetails.defaultProps = {
  messageDetails: null,
};

MessageDetails.propTypes = {
  messageDetails: MessageDetailsShape,
  isLoading: PropTypes.bool.isRequired,
  closeFunc: PropTypes.func.isRequired,
  currentUser: PropTypes.object.isRequired,
  selectedTab: PropTypes.string.isRequired,
  maxHeight: PropTypes.object.isRequired,
};

export default MessageDetails;
